import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

// Авторские операции над объявлениями (тег vision/authoring контракта):
// публикация, «мои объявления» (VD11), правка, снятие. Гейт «активная
// verified-организация» проверяет бэкенд (V1.1-b №3), UI отсекает заведомо
// закрытые состояния на /post.

export type ListingCreate = components['schemas']['ListingCreate'];
export type ListingUpdate = components['schemas']['ListingUpdate'];
export type Listing = components['schemas']['Listing'];
export type ListingStatus = components['schemas']['ListingStatus'];
export type MyListingPage = components['schemas']['MyListingPage'];

/** Ключ-префикс всех запросов лент/карточек — для инвалидации после мутаций. */
const LISTINGS_KEY_PREFIX = ['vision', 'listings'] as const;

/**
 * Опубликовать объявление (`POST /vision/listings`). После успеха
 * инвалидируем ленты — свежее объявление сразу видно в публичной выдаче.
 */
export function useCreateListingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'listings', 'create'],
    mutationFn: (input: ListingCreate) => api.post<Listing>('/api/v1/vision/listings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTINGS_KEY_PREFIX });
    },
  });
}

/**
 * Снять своё объявление с публикации (`POST .../archive`, идемпотентно —
 * повторный клик не ошибка). Доступно с финала публикации и из
 * «Моих объявлений» (V1.3-e2). Обратный путь — unarchive (VD12).
 */
export function useArchiveListingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'listings', 'archive'],
    mutationFn: (listingId: string) =>
      api.post<Listing>(`/api/v1/vision/listings/${listingId}/archive`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTINGS_KEY_PREFIX });
    },
  });
}

/**
 * «Опубликовать снова» (`POST .../unarchive`, VD12): archived → active с
 * bump `published_at` — объявление всплывает в начале публичной ленты.
 * Идемпотентно (повтор по active — 200 без bump'а). Гейт организации как у
 * публикации: 403 `organization_not_verified` / `organization_disabled`;
 * removed автору не возвращается (404 — путь назад только админский restore).
 */
export function useUnarchiveListingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'listings', 'unarchive'],
    mutationFn: (listingId: string) =>
      api.post<Listing>(`/api/v1/vision/listings/${listingId}/unarchive`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTINGS_KEY_PREFIX });
    },
  });
}

const MY_LISTINGS_PAGE_SIZE = 20;

function buildMyListingsUrl(status: ListingStatus | undefined, page: number, size: number): string {
  const search = new URLSearchParams({ page: String(page), page_size: String(size) });
  if (status) search.set('status', status);
  return `/api/v1/vision/my/listings?${search.toString()}`;
}

/**
 * «Мои объявления» (`GET /vision/my/listings`, VD11): все организации
 * вызывающего, все статусы включая removed; `?status=` — опциональный фильтр.
 * Пагинация на бэке страничная (D6), но UI докручивает кнопкой «Показать ещё»
 * (паттерн ленты) — infinite-запрос с page-параметром. Ключ живёт под
 * префиксом лент: мутации create/archive/update инвалидируют и его.
 */
export function useMyListingsQuery(status?: ListingStatus) {
  return useInfiniteQuery({
    queryKey: [...LISTINGS_KEY_PREFIX, 'my', status ?? 'all'],
    queryFn: ({ pageParam }) =>
      api.get<MyListingPage>(buildMyListingsUrl(status, pageParam, MY_LISTINGS_PAGE_SIZE)),
    initialPageParam: 1,
    getNextPageParam: (lastPage: MyListingPage) =>
      lastPage.page * lastPage.page_size < lastPage.total ? lastPage.page + 1 : undefined,
  });
}

/**
 * Авторская карточка своего объявления (`GET /vision/my/listings/{id}`,
 * VD12) — источник страницы правки при прямом заходе по URL (обычный путь —
 * объект через router-state, без запросов). Любой статус, включая removed
 * (страница покажет «недоступно для правки»); нет / чужой организации —
 * единый 404 `listing_not_found`.
 */
export function useMyListingQuery(listingId: string | undefined) {
  return useQuery({
    enabled: Boolean(listingId),
    queryKey: [...LISTINGS_KEY_PREFIX, 'my', 'card', listingId],
    queryFn: () => api.get<Listing>(`/api/v1/vision/my/listings/${listingId}`),
  });
}

/**
 * Правка своего объявления (`PATCH /vision/listings/{id}`, VD9: частичный
 * апдейт; feed/status не меняются; archived править можно — решение V1.1-b №8;
 * removed → 404). После успеха инвалидируем ленты и «мои объявления».
 */
export function useUpdateListingMutation(listingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'listings', 'update', listingId],
    mutationFn: (input: ListingUpdate) =>
      api.patch<Listing>(`/api/v1/vision/listings/${listingId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTINGS_KEY_PREFIX });
    },
  });
}
